import React, { forwardRef, useCallback, useRef, useMemo, useEffect, Fragment, } from 'react';
import Spacings, { createCustomSpacing, } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import styled from '@emotion/styled';
import Portal from '@polestar/component-warehouse-react/components/portal/portal';
import Alignments from '@polestar/component-warehouse-react/tokens/alignments/Alignments';
import { css } from '@emotion/react';
import Backdrop from '@polestar/component-warehouse-react/components/backdrop/Backdrop';
import * as Icons from '@polestar/component-warehouse-react/tokens/icons/Icons';
import { responsiveStyles } from '@polestar/component-warehouse-react/helpers/responsiveStyle';
import IconButton, { IconButtonVariants, } from '@polestar/component-warehouse-react/components/icon-button/IconButton';
import { animate, AnimatePresence, m as motion, useMotionValue, } from 'framer-motion';
import { createTransition } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import { useModal } from '@polestar/component-warehouse-react/contexts/modal';
import LayoutIndexes from '@polestar/component-warehouse-react/tokens/layout-indexes/LayoutIndexes';
import { useFocusInitial, useFocusRestore, useFocusTrap, } from '@polestar/component-warehouse-react/hooks/focus';
import useCombinedRef from '@polestar/component-warehouse-react/hooks/useCombinedRef';
import Sizes from '@polestar/component-warehouse-react/tokens/sizes/Sizes';
import { InvertedThemeProvider, LightThemeProvider, } from '@polestar/component-warehouse-react/components/theme-providers/ThemeProviders';
import { ScrollContext } from '@polestar/component-warehouse-react/components/scroll-observer/ScrollObserver';
import useCallbackRef from '@polestar/component-warehouse-react/hooks/useCallbackRef';
import { useInteractionMode } from '@polestar/component-warehouse-react/contexts/interactionMode';
import { useReducedMotion } from '@polestar/component-warehouse-react/hooks/useReducedMotion';
import { shouldNotForwardProps } from '@polestar/component-warehouse-react/helpers/shouldForwardProps';
import { DrawerProvider } from '@polestar/component-warehouse-react/components/drawer/DrawerContext';
import useMutableValue from '@polestar/component-warehouse-react/hooks/useMutableValue';
import useDimensionsCallback from '@polestar/component-warehouse-react/hooks/useDimensions';
import { GridContext } from '@polestar/component-warehouse-react/components/grid/Grid';
import aria from '@polestar/component-warehouse-react/helpers/accessibility/aria';
import { usePopover } from '@polestar/component-warehouse-react/contexts/popover';
export var DrawerVariants;
(function (DrawerVariants) {
    DrawerVariants["default"] = "default";
    DrawerVariants["wide"] = "wide";
})(DrawerVariants || (DrawerVariants = {}));
/**
 * Used to determine when the animation has finished. Useful for testing purposes.
 */
export var DrawerAnimationState;
(function (DrawerAnimationState) {
    DrawerAnimationState["opened"] = "opened";
    DrawerAnimationState["closed"] = "closed";
})(DrawerAnimationState || (DrawerAnimationState = {}));
/**
 * The duration of the show/hide animation defined in seconds as expected by Framer Motion.
 * @internal
 */
export const drawerHideAnimationDuration = 0.9;
const Drawer = forwardRef(({ detached = true, open = false, position = Alignments.left, children, footer, onClose, withCloseButton = false, isCloseButtonSticky = false, autoFocus = true, closeButtonProps, backdropProps, variant = DrawerVariants.default, style, ...props }, ref) => {
    const internalRef = useRef(null);
    const detachedRef = useRef(null);
    const [scrollCallback, scrollElement] = useCallbackRef();
    const [innerRef, dimensions] = useDimensionsCallback();
    const combinedRef = useCombinedRef(innerRef, internalRef, ref);
    const translateOffsetLeft = useMutableValue(0);
    const { openPopovers } = usePopover();
    useEffect(() => {
        if (position === Alignments.right) {
            translateOffsetLeft.set(-(dimensions.width || 0));
        }
    }, [dimensions, position, translateOffsetLeft]);
    const handleClose = useCallback(() => {
        // Escape, close button
        if (openPopovers.get().length === 0) {
            onClose === null || onClose === void 0 ? void 0 : onClose();
        }
    }, [openPopovers, onClose]);
    const { isKeyboardControlled } = useInteractionMode();
    const isReducedMotion = useReducedMotion();
    const isDisabledMotion = isReducedMotion || isKeyboardControlled;
    const xPosition = useMotionValue(position === Alignments.left ? '-100%' : '100%');
    const dataAnimationOpen = useMotionValue(false);
    dataAnimationOpen.on('change', (value) => {
        var _a;
        // used for easier testing
        (_a = internalRef.current) === null || _a === void 0 ? void 0 : _a.setAttribute('data-animation', `${value ? DrawerAnimationState.opened : DrawerAnimationState.closed}`);
    });
    // prevent that the Drawer is tab-able when hidden #aria
    const xHidden = useMotionValue('none');
    xPosition.on('change', (pos) => {
        if (Math.abs(Number(pos.replace('%', ''))) === 100) {
            xHidden.set('none');
        }
        // TODO: memory leak? too many logs? after opening up and close
        if (Number(pos.replace('%', '')) === 100) {
            dataAnimationOpen.set(false);
        }
        else if (Number(pos.replace('%', '')) === 0) {
            dataAnimationOpen.set(true);
        }
    });
    if (!!open && xHidden.get() === 'none') {
        xHidden.set('flex');
    }
    // Register in the ModalContext
    useModal({
        ref: detachedRef,
        open,
        closeHandler: handleClose,
    });
    useFocusRestore(open, { scrollIntoView: false });
    useFocusTrap(internalRef, detached && open);
    useFocusInitial(internalRef, detached && open && autoFocus, {
        index: withCloseButton ? 1 : 0,
    });
    const closeButtonReference = useMemo(() => (React.createElement(CloseButton, { "data-type": "drawer-close-button", ...closeButtonProps, onClick: handleClose, icon: Icons.close, variant: IconButtonVariants.transparent, drawerVariant: variant, size: Sizes.large, isCloseButtonSticky: isCloseButtonSticky })), [closeButtonProps, handleClose, variant, isCloseButtonSticky]);
    const closeButton = withCloseButton ? closeButtonReference : null;
    const xTransition = createTransition({
        duration: drawerHideAnimationDuration,
        ease: [0.35, 0, 0, 1],
        disabled: isDisabledMotion,
    });
    const animateProps = useMemo(() => (detached ? { style: { x: xPosition, display: xHidden, ...style } } : { style }), [detached, xPosition, xHidden, style]);
    useEffect(() => {
        animate(xPosition, open ? '0%' : `${position === Alignments.left ? -100 : 100}%`, xTransition);
    }, [open, position, xPosition, xTransition, xHidden]);
    const data = (React.createElement(Container, { key: "container", variant: variant, role: "complementary", ...props, ...animateProps, detached: detached, position: position, ref: combinedRef, ...aria({ hidden: !open }) },
        React.createElement(LightThemeProvider, null,
            React.createElement(Inner, { ref: scrollCallback, variant: variant, withCloseButton: withCloseButton, isCloseButtonSticky: isCloseButtonSticky },
                closeButton,
                React.createElement(ScrollContext.Provider, { value: { scrollElement } },
                    React.createElement(DrawerProvider, { translateOffsetLeft: translateOffsetLeft }, variant !== DrawerVariants.wide ? (React.createElement(Fragment, { key: "provider" }, children)) : (
                    // this is needed to ensure any Grid fits the available width while due to the Backdrop there is less space available to show a full grid on the desktop breakpoint
                    React.createElement(GridContext.Provider, { key: "provider", value: {
                            span: { mobile: 4, tablet: 8, desktop: 8, desktopWide: 12 },
                            nested: true,
                        } }, children))))),
            !!footer && (React.createElement(InvertedThemeProvider, null,
                React.createElement(Footer, null, footer))))));
    // aria role chosen by https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/Complementary_role but can be overwritten
    if (detached) {
        return (React.createElement(Portal, null,
            React.createElement(Backdrop, { visible: open, onClose: handleClose, ...backdropProps }),
            React.createElement(AnimatePresence, null, data)));
    }
    return data;
});
export default Drawer;
const Container = styled(motion.div)(({ position: positionSide, detached, theme, variant }) => css(css `
      background-color: ${theme.drawer.backgroundColor};
      height: 100%;
      max-width: 100%;
      flex-shrink: 0;
      display: flex;
      flex-direction: column;
    `, variant === DrawerVariants.default &&
    css `
        ${responsiveStyles({
        width: {
            mobile: '100%',
            tablet: '50vw',
            desktop: '33.33vw',
        },
    })};
      `, variant === DrawerVariants.wide &&
    css `
        ${responsiveStyles({
        width: {
            mobile: '100%',
            tablet: '100%',
            desktop: createCustomSpacing(832 / 16),
            desktopWide: createCustomSpacing(1024 / 16),
        },
    })};

        @media (min-width: 1600px) {
          width: ${createCustomSpacing(1472 / 16)};
        }
      `, detached &&
    css `
        position: fixed;
        inset-block-start: 0;
        ${positionSide}: 0;
        z-index: ${LayoutIndexes.modal};
      `));
const Inner = styled.div(({ variant, withCloseButton, isCloseButtonSticky }) => css(css `
      position: relative;
      width: 100%;
      overflow: auto;
      overflow-x: hidden;
      overscroll-behavior: contain;
      flex: 1;
    `, withCloseButton &&
    isCloseButtonSticky &&
    css `
        height: auto;
      `, !(withCloseButton && isCloseButtonSticky) &&
    css `
        height: 100%;
      `, !withCloseButton &&
    css `
        ${responsiveStyles({
        paddingBlockStart: {
            mobile: Spacings.xxLarge,
            tablet: Spacings.xxxLarge,
            desktopWide: createCustomSpacing(88 / 16),
        },
    })}
      `, variant === DrawerVariants.default &&
    css `
        ${responsiveStyles({
        paddingInlineStart: { mobile: Spacings.large, tablet: Spacings.xLarge },
        paddingInlineEnd: { mobile: Spacings.large, tablet: Spacings.xLarge },
        paddingBlockEnd: { mobile: Spacings.large, tablet: Spacings.xLarge },
    })}
      `, withCloseButton &&
    !isCloseButtonSticky &&
    variant === DrawerVariants.default &&
    css `
        ${responsiveStyles({
        paddingBlockStart: {
            /* due to position:absolute */
            mobile: createCustomSpacing(72 / 16),
            tablet: createCustomSpacing(88 / 16),
        },
    })}
      `, variant === DrawerVariants.wide &&
    css `
        ${responsiveStyles({
        paddingInlineStart: {
            mobile: Spacings.large,
            tablet: Spacings.xxLarge,
            desktop: createCustomSpacing(80 / 16),
            desktopWide: Spacings.xxxxxLarge,
        },
        paddingInlineEnd: {
            mobile: Spacings.large,
            tablet: Spacings.xxLarge,
            desktop: createCustomSpacing(80 / 16),
            desktopWide: Spacings.xxxxxLarge,
        },
        paddingBlockEnd: {
            mobile: Spacings.large,
            tablet: Spacings.xxLarge,
            desktop: createCustomSpacing(80 / 16),
            desktopWide: Spacings.xxxxxLarge,
        },
    })}

        @media (min-width: 1600px) {
          padding: 0 ${createCustomSpacing(128 / 16)} ${createCustomSpacing(128 / 16)}
            ${createCustomSpacing(128 / 16)};
        }
      `, variant === DrawerVariants.wide &&
    css `
        ${responsiveStyles({
        paddingBlockStart: {
            /* due to position:absolute */
            mobile: createCustomSpacing(72 / 16),
            tablet: createCustomSpacing(88 / 16),
        },
    })}
      `));
const Footer = styled.div(({ theme }) => css `
    background-color: ${theme.drawer.footerBackgroundColor};

    ${responsiveStyles({
    padding: {
        mobile: Spacings.medium,
        tablet: Spacings.xLarge,
    },
})}
  `);
const CloseButton = ({ isCloseButtonSticky, drawerVariant, ...props }) => (React.createElement(CloseButtonWrapper, { isCloseButtonSticky: isCloseButtonSticky, variant: drawerVariant, "data-type": "close-button-wrapper" },
    React.createElement(IconButton, { ...props, icon: Icons.close })));
const CloseButtonWrapper = styled('div', shouldNotForwardProps(false, 'isCloseButtonSticky', 'variant'))(({ theme, isCloseButtonSticky, variant }) => css(css `
        position: relative;
        display: flex;
        justify-content: flex-end;
        align-items: center;
      `, isCloseButtonSticky &&
    css `
          position: sticky;
          top: 0;
          background-color: ${theme.dialog.backgroundColor};
          z-index: ${LayoutIndexes.content + 1};
        `, 
// ensure the close button is correctly placed
isCloseButtonSticky &&
    variant !== DrawerVariants.wide &&
    css `
          ${responsiveStyles({
        marginInlineEnd: {
            mobile: `-${Spacings.large}`,
            tablet: `-${Spacings.xLarge}`,
        },
    })}
        `, isCloseButtonSticky &&
    variant === DrawerVariants.default &&
    css `
          ${responsiveStyles({
        padding: {
            mobile: `${Spacings.small}`,
            tablet: `${createCustomSpacing(20 / 16)} ${Spacings.medium};`,
        },
    })}
        `, !isCloseButtonSticky &&
    variant === DrawerVariants.default &&
    css `
          position: absolute;
          inset-inline-start: 0;
          inset-inline-end: 0;

          ${responsiveStyles({
        insetBlockStart: { mobile: Spacings.small, tablet: createCustomSpacing(20 / 16) },
        insetInlineEnd: { mobile: Spacings.xSmall, tablet: Spacings.medium },
    })}
        `, variant === DrawerVariants.wide &&
    css `
          position: absolute;
          inset-inline-start: 0;
          inset-inline-end: 0;
          inset-block-start: 0;

          ${responsiveStyles({
        padding: {
            mobile: `${Spacings.small} ${Spacings.xSmall};`,
            tablet: `${createCustomSpacing(20 / 16)} ${Spacings.medium};`,
        },
    })}
        `));
